import * as React from "react"
import { MoreHorizontal, Users, Settings, Copy, UserPlus } from "lucide-react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { formatCurrency } from "@/lib/utils"
import type { Group, User } from "@/types/domain.types"
import { logger } from "@/lib/logger"

interface GroupCardProps {
  group: Group
  currentUser: User
  onSelect?: (group: Group) => void
  onEdit?: (group: Group) => void
  onInvite?: (group: Group) => void
  onSettings?: (group: Group) => void
  showActions?: boolean
  isSelected?: boolean
}

export const GroupCard: React.FC<GroupCardProps> = ({
  group,
  currentUser: _currentUser,
  onSelect,
  onEdit,
  onInvite,
  onSettings,
  showActions = true,
  isSelected = false,
}) => {
  const memberCount = group.members?.length || 0
  const totalExpenses = group.total_expenses || 0
  const userBalance = group.user_balance || 0

  const handleClick = () => {
    if (onSelect) {
      onSelect(group)
    }
  }

  const handleCopyInviteCode = async () => {
    if (group.invite_code) {
      try {
        await navigator.clipboard.writeText(group.invite_code)
        // Could show a toast notification here
      } catch (error) {
        logger.error("Failed to copy invite code:", error)
      }
    }
  }

  return (
    <Card
      className={`transition-all cursor-pointer hover:shadow-md ${
        isSelected ? 'ring-2 ring-primary' : ''
      }`}
      onClick={handleClick}
    >
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
              {group.emoji ? (
                <span className="text-2xl">{group.emoji}</span>
              ) : (
                <Users className="h-6 w-6 text-primary" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <CardTitle className="text-lg truncate">{group.name}</CardTitle>
              <CardDescription className="flex items-center gap-2">
                <Users className="h-3 w-3" />
                {memberCount} member{memberCount !== 1 ? 's' : ''}
              </CardDescription>
            </div>
          </div>
          {showActions && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {onEdit && (
                  <DropdownMenuItem onClick={(e) => {
                    e.stopPropagation()
                    onEdit(group)
                  }}>
                    <Settings className="mr-2 h-4 w-4" />
                    Edit Group
                  </DropdownMenuItem>
                )}
                {onInvite && (
                  <DropdownMenuItem onClick={(e) => {
                    e.stopPropagation()
                    onInvite(group)
                  }}>
                    <UserPlus className="mr-2 h-4 w-4" />
                    Invite Members
                  </DropdownMenuItem>
                )}
                {group.invite_code && (
                  <DropdownMenuItem onClick={(e) => {
                    e.stopPropagation()
                    handleCopyInviteCode()
                  }}>
                    <Copy className="mr-2 h-4 w-4" />
                    Copy Invite Code
                  </DropdownMenuItem>
                )}
                {onSettings && (
                  <DropdownMenuItem onClick={(e) => {
                    e.stopPropagation()
                    onSettings(group)
                  }}>
                    <Settings className="mr-2 h-4 w-4" />
                    Settings
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Financial Summary */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-sm text-muted-foreground">Total Spent</div>
            <div className="font-semibold">
              {formatCurrency(totalExpenses, group.currency || 'EUR')}
            </div>
          </div>
          <div>
            <div className="text-sm text-muted-foreground">
              {userBalance >= 0 ? "You're owed" : "You owe"}
            </div>
            <div className={`font-semibold ${
              userBalance >= 0 ? 'text-green-600' : 'text-red-600'
            }`}>
              {formatCurrency(Math.abs(userBalance), group.currency || 'EUR')}
            </div>
          </div>
        </div>

        {/* Recent Members */}
        {group.members && group.members.length > 0 && (
          <div>
            <div className="text-sm text-muted-foreground mb-2">Members</div>
            <div className="flex -space-x-2">
              {group.members.slice(0, 4).map((member) => (
                <Avatar key={member.id} className="h-8 w-8 border-2 border-background">
                  <AvatarImage src={member.avatar_url} />
                  <AvatarFallback className="text-xs">
                    {member.name.charAt(0)}
                  </AvatarFallback>
                </Avatar>
              ))}
              {group.members.length > 4 && (
                <div className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-background bg-muted text-xs font-medium">
                  +{group.members.length - 4}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Pinned Categories */}
        {group.pinned_categories && group.pinned_categories.length > 0 && (
          <div>
            <div className="text-sm text-muted-foreground mb-2">Quick Categories</div>
            <div className="flex flex-wrap gap-1">
              {group.pinned_categories.slice(0, 3).map((categoryId, index) => (
                <Badge key={categoryId} variant="secondary" className="text-xs">
                  Category {index + 1}
                </Badge>
              ))}
              {group.pinned_categories.length > 3 && (
                <Badge variant="secondary" className="text-xs">
                  +{group.pinned_categories.length - 3} more
                </Badge>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}